import React from "react";
import { matrixTransformation } from "../util/matrixTransformation";
import { bradleyTerry } from "../util/bradleyTerry";
import { thurstoneMosteller } from "../util/thurstoneMosteller";

// design
const HEADER_BG = "#374151";
const CARD     = { borderRadius: "8px", marginTop: "16px", width: "fit-content", boxShadow: "0 2px 6px rgba(0,0,0,0.08)" };
const CARD_HDR = { background: HEADER_BG, padding: "8px 14px", display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px 8px 0 0" };
const SECTION  = { borderLeft: "1px solid #cbd5e1", borderRight: "1px solid #cbd5e1" };
const FOOTER   = { ...SECTION, borderBottom: "1px solid #cbd5e1", borderRadius: "0 0 8px 8px", padding: "5px 14px 6px", background: "#f9fafb", borderTop: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280", display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" };
const TH = {
    background: HEADER_BG, color: "white",
    padding: "8px 14px", fontWeight: "600", fontSize: "13px",
    textAlign: "center", whiteSpace: "nowrap"
};
const CELL = {
    padding: "7px 14px", fontSize: "13px",
    textAlign: "center", verticalAlign: "middle",
    borderRight: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb"
};

function sigStars(p) {
    if (p < 0.001) return " ***";
    if (p < 0.01)  return " **";
    if (p < 0.05)  return " *";
    return "";
}

function pStyle(val) {
    if (val < 0.001) return { background: "#bbf7d0", color: "#14532d", fontWeight: "700" };
    if (val < 0.05)  return { background: "#ecfdf5", color: "#166534", fontWeight: "600" };
    return { color: "#4b5563" };
}

function zStyle(val) {
    if (Math.abs(val) >= 1.96) return { color: "#1d4ed8", fontWeight: "700" };
    return { color: "#6b7280" };
}

const COLUMN_TIPS = {
    "Rank":           "Methods sorted by β coefficient (quality score)",
    "Method":         "Name of the evaluated method",
    "Total Score":    "Total number of wins across all pairwise comparisons",
    "β (beta)":       "Estimated quality score from the model (higher = better perceived quality)",
    "Standard Error": "Uncertainty of the β estimate",
    "z-score":        "β divided by its SE: values beyond ±1.96 are statistically significant (p < 0.05)",
    "p-value":        "Two-tailed p-value testing whether β differs significantly from zero"
};

export default function ResultsTable({ data, modelType }) {
    // fallback
    if (!data || !data.twoAfcData) return null;

    const transformed = matrixTransformation(data);
    const methods = transformed.sortedMethods || [];
    const model = modelType === "BT" ? bradleyTerry(transformed) : thurstoneMosteller(transformed);

    const betas      = model.betas || [];
    const se         = model.se    || [];
    const z          = model.z     || [];
    const p          = model.p     || [];
    const totalScore = transformed.totalScore || {};

    const ranked = methods
        .map((m, i) => ({ m, i }))
        .sort((a, b) => betas[b.i] - betas[a.i]);
    const rankOf = {};
    ranked.forEach(({ m }, r) => { rankOf[m] = r + 1; });

    const bestIdx       = betas.length ? betas.indexOf(Math.max(...betas)) : -1;
    const sortedIndices = ranked.map(({ i }) => i);
    const columns       = ["Rank", "Method", "Total Score", "β (beta)", "Standard Error", "z-score", "p-value"];
    const modelTag      = modelType === "BT" ? "Bradley–Terry" : "Thurstone–Mosteller";
    const sigCount      = p.filter(v => v !== undefined && v < 0.05).length;

    return (
        <div style={CARD}>

            <div style={CARD_HDR}>
                <h4 style={{ margin: 0, color: "white", fontSize: "13px", fontWeight: "600" }}>Results Table</h4>
                <span style={{ color: "rgba(255,255,255,0.8)", fontSize: "10px", padding: "1px 6px", borderRadius: "4px" }}>
                    {modelTag}
                </span>
                {bestIdx >= 0 && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: "6px", alignItems: "center" }}>
                        <span style={{ background: "#1d4ed8", color: "white", fontSize: "11px", fontWeight: "700", padding: "2px 8px", borderRadius: "10px" }}>
                            ★ {methods[bestIdx]}
                        </span>
                        {sigCount > 0 && (
                            <span style={{ background: "#16a34a", color: "white", fontSize: "11px", fontWeight: "600", padding: "2px 8px", borderRadius: "10px" }}>
                                {sigCount} significant
                            </span>
                        )}
                    </span>
                )}
            </div>

            <div style={{ ...SECTION, padding: "4px 14px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "11px", color: "#6b7280" }}>
                Methods ranked by quality score β. <strong style={{ color: "#1d4ed8" }}>Blue</strong> z-scores (|z| ≥ 1.96) indicate statistical significance.
            </div>

            {/* table */}
            <div style={{ ...SECTION, background: "white", overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                        <tr>
                            {columns.map((h, ci) => (
                                <th
                                    key={ci}
                                    title={COLUMN_TIPS[h] || ""}
                                    style={{
                                        ...TH,
                                        cursor: "help",
                                        borderRight: ci < columns.length - 1 ? "1px solid #4b5563" : "none",
                                        borderBottom: "2px solid #1f2937"
                                    }}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedIndices.map((i) => {
                            const m        = methods[i];
                            const isWinner = i === bestIdx;
                            const rowBg    = isWinner ? "#eff6ff" : (i % 2 === 0 ? "white" : "#f8fafc");
                            return (
                                <tr key={i} style={{ background: rowBg }}>
                                    <td style={{ ...CELL, color: "#9ca3af", fontSize: "12px" }}>#{rankOf[m]}</td>
                                    <td style={{ ...CELL, fontWeight: "700", color: isWinner ? "#1d4ed8" : "#374151", borderRight: "2px solid #cbd5e1" }}>
                                        {m}{isWinner ? " ★" : ""}
                                    </td>
                                    <td style={CELL}>{totalScore[m]}</td>
                                    <td style={{ ...CELL, fontWeight: isWinner ? "700" : "normal", color: isWinner ? "#1d4ed8" : "inherit" }}>
                                        {betas[i]?.toFixed(4)}
                                    </td>
                                    <td style={{ ...CELL, color: "#6b7280" }}>{se[i]?.toFixed(4)}</td>
                                    <td
                                        title={Math.abs(z[i]) >= 1.96 ? "Statistically significant (|z| ≥ 1.96)" : "Not statistically significant (|z| < 1.96)"}
                                        style={{ ...CELL, ...zStyle(z[i]), cursor: "help" }}
                                    >
                                        {z[i]?.toFixed(4)}
                                    </td>
                                    <td
                                        title={
                                            p[i] < 0.001 ? "Highly significant (p < 0.001) ***" :
                                            p[i] < 0.01  ? "Very significant (p < 0.01) **" :
                                            p[i] < 0.05  ? "Significant (p < 0.05) *" :
                                            "Not significant (p ≥ 0.05)"
                                        }
                                        style={{ ...CELL, ...pStyle(p[i]), borderRight: "none", cursor: "help" }}
                                    >
                                        {p[i] < 0.0001 ? "< 0.0001" : p[i]?.toFixed(4)}{sigStars(p[i])}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div style={FOOTER}>
                <span>Hover cells for details</span>
                <span>|</span>
                <span>★ best method</span>
                <span>|</span>
                <span><b style={{ color: "#1d4ed8" }}>blue</b> = |z| ≥ 1.96</span>
                <span>|</span>
                <span><b style={{ color: "#166534" }}>green</b> = p &lt; 0.05</span>
                <span>|</span>
                <span>*** p&lt;0.001 &nbsp; ** p&lt;0.01 &nbsp; * p&lt;0.05</span>
            </div>
        </div>
    );
}
